import React, { useMemo, useState } from "react";
import {
  MRT_Row,
  MRT_TableOptions,
  type MRT_ColumnDef,
} from "material-react-table";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
  CircularProgress,
  Typography,
  TextField,
} from "@mui/material";
import { AdminGlobalTable } from "../../../tables/AdminGlobalTable";
import moment from "moment";
import { useKeycloak } from "@react-keycloak/web";
import { useUpdateCohortPeriod } from "../../../../tanstackQuery/queries/superAdmin/group/period";
import { toast } from "sonner";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAtom } from "jotai";
import { SAdminBaseAtom } from "../../../../stores/jotai/super-admin/superAdminBase";
import { useQueryClient } from "@tanstack/react-query";
import { inputCommonStyle } from "../../../../util/admin/cssCommonStyle";
type Period = {
  endDate: string;
  goal: number;
};

const CohortPeriodsTable = ({ cohortPeriodData, handleClose }) => {
  const theme = useTheme();
  const { keycloak } = useKeycloak();
  const [cohortPeriods, setCohortPeriods] = useState(cohortPeriodData);
  const [sAdminBase] = useAtom(SAdminBaseAtom);
  const client = useQueryClient();
  const {
    mutateAsync: updateCohortPeriod,
    isPending: isUpdatePending,
    isSuccess: isUpdateSuccess,
  } = useUpdateCohortPeriod(
    { accessToken: keycloak?.token },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["group-period"] });
        handleClose()
      },
    }
  );
  const cohortID = sAdminBase?.selectedCohort?.group?.groupID;
  const handleCreateCohortPeriod: MRT_TableOptions<Period>["onCreatingRowSave"] =
    async ({ values, table }) => {
      const newPeriod: Period = {
        ...values,
        goal: Number(values.goal),
      };
      setCohortPeriods([newPeriod, ...cohortPeriods]);
      table.setCreatingRow(null);
    };
  const handleUpdateCohortPeriod: MRT_TableOptions<Period>["onEditingRowSave"] =
    async ({ values, row, table }) => {
      const updatePeriod: Period = {
        ...values,
        goal: Number(values.goal),
      };
      const updatedRowData = { ...row.original, ...updatePeriod };
      const updatedData = cohortPeriods.filter(
        (data) => data.groupPeriodID !== row.original.groupPeriodID
      );
      setCohortPeriods([...updatedData, updatedRowData]);
      table.setEditingRow(null);
    };
  const deleteCohortPeriod = (row) => {
    const deleteCohortPeriod = cohortPeriods.filter(
      (data) => data.groupPeriodID !== row.original.groupPeriodID
    );
    setCohortPeriods(deleteCohortPeriod);
  };
  const changedCohortPeriodData = async () => {
    const payload = {
      cohortID,
      periods: cohortPeriods,
      periodLabel: "Week",
    };
    try {
      const response = await updateCohortPeriod(payload);
      if (response?.payload?.isUpdated) {
        toast.success("Cohort Period updated successfully!");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update Cohort Period.");
    }
  };
  const columns = useMemo<MRT_ColumnDef<Period>[]>(
    () => [
      {
        header: "Period End Date",
        accessorKey: "endDate",
        accessorFn: (row) => new Date(row.endDate),
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: "date",
          required: true,
          fullWidth: true,
        }),
        Cell: ({ cell }) => {
          const dateString = cell.getValue();
          return moment(dateString).format("MM/DD/YYYY h:mm A");
        },
      },
      {
        header: "Interview Goal",
        accessorKey: "goal",
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: "number",
          required: true,
          fullWidth: true,
          inputProps: {
            min: 0,
          },
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const num = e.target.valueAsNumber;
            cell.row._valuesCache["goal"] = isNaN(num) ? 0 : num;
          },
        }),
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
    ],
    []
  );
  return (
    <Box>
      <AdminGlobalTable
        columns={columns}
        data={cohortPeriods}
        config={{
          enablePagination: false,
          enableRowVirtualization: false,
          enableColumnVirtualization: false,
          enableStickyHeader: true,
          enableColumnFilters: false,
          enableGlobalFilter: false,
          muiSelectCheckboxProps: { color: "secondary" },
          createDisplayMode: "row",
          editDisplayMode: "row",
          enableEditing: true,
          onCreatingRowSave: handleCreateCohortPeriod,
          onEditingRowSave: handleUpdateCohortPeriod,

          positionActionsColumn: "last",
          displayColumnDefOptions: {
            "mrt-row-actions": {
              header: "",
              muiTableHeadCellProps: {
                sx: { textAlign: "right" },
              },
              muiTableBodyCellProps: {
                sx: {
                  "& .MuiIconButton-root": {
                    margin: 0,
                    padding: 0,
                  },
                },
              },
            },
          },

          muiTableBodyRowProps: ({ row }) => ({
            hover: false,
            sx: {
              width: "100%",
              flexGrow: 1,
              backgroundColor: "transparent",
              ":hover": { backgroundColor: theme.custom?.hoverOptionColor },
            },
          }),
          muiTableBodyCellProps: ({ cell, column, table }) => ({
            onClick: () => {
              table.setEditingCell(cell);
              queueMicrotask(() => {
                const textField = table.refs.editInputRefs.current?.[column.id];
                if (textField) {
                  textField.focus();
                  textField.select?.();
                }
              });
            },
            sx: {
              width: "100% !important",
              height: "auto !important",
              border: "none !important",
              "&:hover": {
                border: "none !important",
                boxShadow: "none !important",
              },
            },
          }),
          muiTableContainerProps: {
            sx: {
              width: "100%",
              maxHeight: "calc(100vh - 500px)",
              minHeight: "calc(100vh - 500px)",
            },
          },

          renderTopToolbarCustomActions: ({ table }) => (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Stack
                direction="row"
                gap={3}
                sx={{
                  height: "auto",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <Box>
                  <Typography fontWeight={700}>Period Label</Typography>
                </Box>
                <Box>
                  <TextField
                    id="outlined-basic"
                    variant="outlined"
                    size="small"
                    label="Week"
                    sx={inputCommonStyle}
                  />
                </Box>
              </Stack>
              <Button
                variant="contained"
                onClick={() => {
                  table.setCreatingRow(true);
                }}
              >
                Add Period
              </Button>
            </Box>
          ),
          renderRowActions: ({ row, table }) => (
            <Stack direction="row" gap={1}>
              <Tooltip title="Edit">
                <IconButton onClick={() => table.setEditingRow(row)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton>
                  <DeleteIcon onClick={() => deleteCohortPeriod(row)} />
                </IconButton>
              </Tooltip>
            </Stack>
          ),
        }}
      />
      <Stack direction="row" justifyContent="flex-end" gap={1}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          disabled={isUpdatePending}
          variant="contained"
          onClick={changedCohortPeriodData}
          endIcon={
            isUpdatePending ? (
              <CircularProgress size={15} color="primary" />
            ) : null
          }
        >
          Update
        </Button>
      </Stack>
    </Box>
  );
};

export default CohortPeriodsTable;
